import { Injectable } from '@angular/core';
import { CanActivate, CanActivateChild, ActivatedRouteSnapshot, RouterStateSnapshot, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { MenuGroupModel } from 'src/Models/MenuGroupModel';
import { I360Service } from 'src/Services/i360Service';

@Injectable()
export class AuthGuard implements CanActivate, CanActivateChild {
    menuModel: MenuGroupModel[];

    private readonly dedicatedRoots = [
        'claim', 'wireless', 'ipr', 'voip', 'rmd', 'ipcs', 'trac-it', 'library'
    ];

    // In-app route families keyed by menu productId. Used only as extra candidates
    // on top of each menu item's own externalURL — not hard-coded page ifs.
    private readonly productRoutePrefixes: { [productId: number]: string[] } = {
        1: ['/wireless'],
        2: ['/ipr'],
        3: ['/voip'],
        4: ['/trac-it'],
        7: ['/view/briefingservices', '/view/briefingdetails'],
        8: ['/view/rulesview', '/view/crrahome', '/view/myfavourite', '/view/updates', '/view/deposit-rate'],
        9: ['/view/contentview', '/view/updateview', '/view/home', '/view/about', '/view/mytrfafavorite'],
        12: ['/carriertariffs'],
        13: ['/statecommission'],
        15: ['/library/cpcn/certificates'],
        16: ['/library/secretaryofstatesauthorization'],
        17: ['/library/exemption/certificates'],
        21: ['/rmd'],
        23: ['/training'],
        24: ['/view/ratelookup', '/view/futurechanges', '/view/911updates', '/view/update', '/view/myfavorite'],
        27: ['/specialbriefingreport'],
        28: ['/webinarlibrary'],
        29: ['/whitepapers'],
        33: ['/trscustomer', '/view/trsfavorite'],
        35: ['/jsiresources'],
        36: ['/view/view47', '/view/checklist'],
        37: ['/view/company-info', '/view/add-comp-que', '/view/attach-to-determine-services',
            '/view/state-selection', '/view/client-profile', '/view/add-company-info',
            '/view/sensitive-info', '/view/portal-credential', '/view/notes',
            '/view/onboarding-state-selection', '/view/attachments'],
        38: ['/view/quotes-and-proposal', '/view/client-quote', '/view/client-notes'],
        40: ['/taxexemptionforms'],
        41: ['/ipcs'],
        54: ['/claim', '/view/lifeline']
    };

    constructor(
        private readonly router: Router,
        private readonly i360Service: I360Service
    ) { }

    canActivate(
        next: ActivatedRouteSnapshot,
        state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> {
        return this.authorize(state);
    }

    canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> {
        return this.canActivate(route, state);
    }

    private authorize(state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> {
        const requestUrl = this.getRequestedUrl(state);

        if (this.isPublicRoute(requestUrl)) {
            return true;
        }

        if (!this.isUserAuthenticated()) {
            return this.router.createUrlTree(['/login']);
        }

        this.hydrateSessionFromLocalStorage();

        this.menuModel = this.getUserMenu();
        if (this.menuModel && this.menuModel.length > 0) {
            return this.evaluatePagePermission(requestUrl, this.menuModel);
        }

        const userId = this.getUserId();
        const tokenKey = sessionStorage.getItem('tokenValue') || localStorage.getItem('tokenValue') || '';
        if (!userId) {
            return this.router.createUrlTree(['/login']);
        }

        return this.i360Service.i360MenuGroupGet(tokenKey, userId).pipe(
            map(data => {
                const menu = data && data['listOfMenu'] ? data['listOfMenu'] : [];
                sessionStorage.setItem('UserManu', JSON.stringify(menu));
                localStorage.setItem('UserManu', JSON.stringify(menu));
                this.menuModel = menu;
                if (!menu || menu.length === 0) {
                    return this.accessDeniedTree('selected');
                }
                return this.evaluatePagePermission(requestUrl, menu);
            }),
            catchError(() => {
                return of(this.accessDeniedTree('selected'));
            })
        );
    }

    private evaluatePagePermission(requestUrl: string, menuModel: MenuGroupModel[]): boolean | UrlTree {
        const menuItem = this.findMatchingMenuItem(requestUrl, menuModel);
        if (!menuItem) {
            return true;
        }

        if (this.isMenuItemLocked(menuItem)) {
            return this.accessDeniedTree(menuItem.displayName);
        }

        return true;
    }

    private findMatchingMenuItem(requestUrl: string, menuModel: MenuGroupModel[]): any {
        if (!menuModel || menuModel.length === 0) {
            return null;
        }

        let matchedItem = null;
        let matchedLength = -1;

        for (let i = 0; i < menuModel.length; i++) {
            const children = menuModel[i].childMenu;
            if (!children || children.length === 0) {
                continue;
            }

            for (let j = 0; j < children.length; j++) {
                const item = children[j];
                const score = this.matchScore(requestUrl, item);
                if (score > matchedLength) {
                    matchedLength = score;
                    matchedItem = item;
                }
            }
        }

        return matchedItem;
    }

    private matchScore(requestUrl: string, item: any): number {
        const candidates = this.getMatchCandidates(item);
        let best = -1;

        for (let k = 0; k < candidates.length; k++) {
            if (this.pathMatches(requestUrl, candidates[k])) {
                const len = this.normalizeUrl(candidates[k]).length;
                if (len > best) {
                    best = len;
                }
            }
        }

        const menuPath = this.extractInAppPath(item ? item.externalURL : '');
        if (menuPath) {
            if (this.sharesDedicatedRoot(requestUrl, menuPath) || this.sharesDistinctiveToken(requestUrl, menuPath)) {
                const len = menuPath.length;
                if (len > best) {
                    best = len;
                }
            }
        }

        return best;
    }

    private getMatchCandidates(item: any): string[] {
        const candidates: string[] = [];
        const inAppPath = this.extractInAppPath(item ? item.externalURL : '');
        if (inAppPath) {
            candidates.push(inAppPath);
        }

        const productId = item ? Number(item.productId) : NaN;
        const prefixes = this.productRoutePrefixes[productId];
        if (prefixes) {
            for (let i = 0; i < prefixes.length; i++) {
                candidates.push(prefixes[i]);
            }
        }

        return candidates;
    }

    private extractInAppPath(externalURL: any): string {
        if (!externalURL || externalURL === 'null') {
            return '';
        }

        let value = String(externalURL).trim();
        const hashIndex = value.indexOf('#');
        if (hashIndex !== -1) {
            return this.normalizeUrl(value.substring(hashIndex + 1));
        }

        if (/^https?:\/\//i.test(value)) {
            return '';
        }

        if (value.indexOf('.') !== -1) {
            return '';
        }

        return this.normalizeUrl(value);
    }

    private isMenuItemLocked(item: any): boolean {
        if (!item) {
            return false;
        }
        const locked = item.isLocked;
        return locked === true || locked === 1 || String(locked).toLowerCase() === 'true';
    }

    private accessDeniedTree(displayName: string): UrlTree {
        let name = displayName || 'selected';
        if (name.indexOf('Tariffs.') > -1 || name.indexOf('Tarrifs.') > -1) {
            name = 'TariffsNet';
        }
        const para = encodeURI(name.replace(/\s+/g, '-'));
        return this.router.createUrlTree(['/Access/Denied', para]);
    }

    private isUserAuthenticated(): boolean {
        const sessionUserId = sessionStorage.getItem('UserId');
        const localUserId = localStorage.getItem('UserId');
        const hasUser = this.hasUserId(sessionUserId) || this.hasUserId(localUserId);
        const isLogin =
            sessionStorage.getItem('isLogin') === 'true' ||
            localStorage.getItem('isLogin') === 'true';
        return hasUser && isLogin;
    }

    private hasUserId(userId: string): boolean {
        return !!userId && userId !== '0' && userId !== 'null' && userId !== 'undefined';
    }

    private getUserId(): any {
        if (this.hasUserId(sessionStorage.getItem('UserId'))) {
            return sessionStorage.getItem('UserId');
        }
        if (this.hasUserId(localStorage.getItem('UserId'))) {
            return localStorage.getItem('UserId');
        }
        return null;
    }

    private getUserMenu(): MenuGroupModel[] {
        let raw = sessionStorage.getItem('UserManu');
        if (!raw) {
            raw = localStorage.getItem('UserManu');
            if (raw) {
                sessionStorage.setItem('UserManu', raw);
            }
        }
        if (!raw) {
            return [];
        }
        try {
            const parsed = JSON.parse(raw);
            return parsed && parsed.length >= 0 ? parsed : [];
        } catch (e) {
            return [];
        }
    }

    private hydrateSessionFromLocalStorage(): void {
        if (!sessionStorage.getItem('UserId') && localStorage.getItem('UserId')) {
            sessionStorage.setItem('UserId', localStorage.getItem('UserId'));
        }
        if (!sessionStorage.getItem('isLogin') && localStorage.getItem('isLogin')) {
            sessionStorage.setItem('isLogin', localStorage.getItem('isLogin'));
        }
        if (!sessionStorage.getItem('UserManu') && localStorage.getItem('UserManu')) {
            sessionStorage.setItem('UserManu', localStorage.getItem('UserManu'));
        }
        if (!sessionStorage.getItem('tokenValue') && localStorage.getItem('tokenValue')) {
            sessionStorage.setItem('tokenValue', localStorage.getItem('tokenValue'));
        }
        if (!sessionStorage.getItem('EncyUserId') && localStorage.getItem('EncyUserId')) {
            sessionStorage.setItem('EncyUserId', localStorage.getItem('EncyUserId'));
        }
    }

    private getRequestedUrl(state: RouterStateSnapshot): string {
        if (state && state.url) {
            return state.url;
        }
        if (window.location.hash) {
            return window.location.hash.replace('#', '');
        }
        return this.router.url || '';
    }

    private isPublicRoute(url: string): boolean {
        const path = this.normalizeUrl(url);
        if (path.indexOf('/admin') === 0) {
            return true;
        }

        const publicExact = [
            '/index',
            '/registration',
            '/login',
            '/forgotpassword',
            '/createpassword',
            '/news'
        ];
        if (publicExact.indexOf(path) !== -1) {
            return true;
        }

        const publicPrefixes = [
            '/login/',
            '/access/denied',
            '/emailverification',
            '/verify/',
            '/adminclientlogin',
            '/userclientlogin',
            '/view/downloadfiles',
            '/view/downloadtrfafiles',
            '/view/download-quotes-and-proposal',
            '/view/download-quotes'
        ];
        for (let i = 0; i < publicPrefixes.length; i++) {
            if (path === publicPrefixes[i] || path.indexOf(publicPrefixes[i]) === 0) {
                return true;
            }
        }

        if (path.indexOf('/changepassword/') === 0) {
            return true;
        }

        return false;
    }

    private pathMatches(requestUrl: string, candidate: string): boolean {
        const url = this.normalizeUrl(requestUrl);
        const prefix = this.normalizeUrl(candidate);
        if (!prefix || prefix === '/' || prefix === '/null') {
            return false;
        }
        if (url === prefix || url.indexOf(prefix + '/') === 0) {
            return true;
        }

        const urlSegs = this.pathSegments(url);
        const prefixSegs = this.pathSegments(prefix);
        if (prefixSegs.length === 0 || urlSegs.length < prefixSegs.length) {
            return false;
        }

        for (let i = 0; i < prefixSegs.length - 1; i++) {
            if (urlSegs[i] !== prefixSegs[i]) {
                return false;
            }
        }

        const lastPrefix = prefixSegs[prefixSegs.length - 1];
        const lastUrl = urlSegs[prefixSegs.length - 1];
        if (lastUrl === lastPrefix) {
            return true;
        }
        if (lastPrefix.length >= 6 && lastUrl.indexOf(lastPrefix) === 0) {
            const nextChar = lastUrl.charAt(lastPrefix.length);
            return nextChar === '-';
        }
        return false;
    }

    private sharesDedicatedRoot(requestUrl: string, menuPath: string): boolean {
        const requestRoot = this.pathSegments(this.normalizeUrl(requestUrl))[0] || '';
        const menuRoot = this.pathSegments(menuPath)[0] || '';
        return !!requestRoot && requestRoot === menuRoot && this.dedicatedRoots.indexOf(requestRoot) !== -1;
    }

    private sharesDistinctiveToken(requestUrl: string, menuPath: string): boolean {
        const requestTokens = this.distinctiveTokens(requestUrl);
        const menuTokens = this.distinctiveTokens(menuPath);
        for (let i = 0; i < requestTokens.length; i++) {
            for (let j = 0; j < menuTokens.length; j++) {
                const a = requestTokens[i];
                const b = menuTokens[j];
                if (a === b) {
                    return true;
                }
                if (a.indexOf(b) === 0 || b.indexOf(a) === 0) {
                    return true;
                }
            }
        }
        return false;
    }

    private distinctiveTokens(url: string): string[] {
        const generic = [
            'view', 'list', 'details', 'detail', 'reports', 'report', 'import',
            'summary', 'yearly', 'home', 'about', 'update', 'updates', 'notes',
            'info', 'favorite', 'favorites', 'my', 'claim','matrix'
        ];
        const segs = this.pathSegments(this.normalizeUrl(url));
        const tokens: string[] = [];
        for (let i = 0; i < segs.length; i++) {
            const seg = segs[i];
            const head = seg.split('-')[0];
            if (seg.length >= 6 && generic.indexOf(seg) === -1) {
                tokens.push(seg);
            }
            if (head.length >= 6 && generic.indexOf(head) === -1 && tokens.indexOf(head) === -1) {
                tokens.push(head);
            }
        }
        return tokens;
    }

    private pathSegments(url: string): string[] {
        const normalized = this.normalizeUrl(url);
        const parts = normalized.split('/');
        const segs: string[] = [];
        for (let i = 0; i < parts.length; i++) {
            if (parts[i]) {
                segs.push(parts[i]);
            }
        }
        return segs;
    }

    private normalizeUrl(url: string): string {
        if (!url) {
            return '';
        }
        let value = String(url).split('?')[0].split('#')[0];
        value = value.replace(/^#/, '');
        if (value.charAt(0) !== '/') {
            value = '/' + value;
        }
        if (value.length > 1 && value.charAt(value.length - 1) === '/') {
            value = value.substring(0, value.length - 1);
        }
        return value.toLowerCase();
    }
}
